import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "../App.css";
import "./PublicSite.css";
import { CompanyIcon, EmailIcon, FilterIcon, InfoIcon, LicenseIcon, PhoneIcon, VerificationIcon } from "../assets/icons/icons";
import clearBadgeMarkWhite from "../assets/clearbadge-icon-white.png";
import { supabase } from "../supabaseClient";
import TurnstileWidget from "../components/TurnstileWidget";
import LandingFooter from "../components/LandingFooter";
import { mergeCategoriesByIndustry } from "../constants/licenseCategories";
import { trackGoal } from "../utils/datafast";
import { sortLicensesByState, sortStateCodes } from "../utils/licenses";
import useFocusTrap from "../hooks/useFocusTrap";

const API_BASE = process.env.REACT_APP_API_URL || "";
const LOGO_WHITE_PUBLIC_PATH = "/clearbadge-logo-white.png";
const resolveTurnstileSiteKey = () => {
  const key = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
  if (typeof window === "undefined") return key;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "";
  return key;
};
const TURNSTILE_SITE_KEY = resolveTurnstileSiteKey();

const STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME",
  "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA",
  "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

const LICENSE_STATUS_OPTIONS = [
  { value: "any", label: "Any status" },
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "expiring", label: "Expiring" },
  { value: "issues", label: "Issues" },
  { value: "unverified", label: "Unverified" },
];

const SPONSORED_SLOTS = 5;
const FILTER_ANIMATION_MS = 220;
const DEFAULT_FILTERS = { verifiedOnly: false, licenseStatus: "any" };
const PAGE_SIZES = [10, 20, 50, 100];

const VERIFICATION_TOOLTIP_COPY =
  "Verification confirms identity and license status through ClearBadge. It does not imply endorsement, quality, or suitability.";
const UNVERIFIED_TOOLTIP_COPY =
  "Not Verified means this professional has not completed ClearBadge's verification process. It does not imply lack of licensure, quality, or suitability.";
const RESULTS_PAID_FEATURES_DISCLOSURE =
  "Some professionals provide additional profile information or features through paid ClearBadge services. Paid features do not affect verification status or imply endorsement.";
const SPONSORED_TOOLTIP_COPY =
  "Sponsored professionals are Elite members who have purchased enhanced visibility. Sponsored placement does not reflect professional qualifications, endorsement, or verification status.";
const FEATURED_TOOLTIP_COPY =
  "Featured professionals have paid for enhanced visibility. This designation does not reflect professional qualifications or endorsement.";
const HERO_VIDEO_ID = "5XzouxyzhJw";
const TURNSTILE_GATE_TIMEOUT_MS = 15000;

const getTurnstileErrorMessage = (code) => {
  const normalized = String(code || "").trim();
  if (!normalized) return "Security check failed. Please try your search again.";
  if (normalized === "timeout" || normalized === "Turnstile load timeout") {
    return "Security check timed out. Please try your search again.";
  }
  return `Security check failed (${normalized}). Please try your search again.`;
};

const getYoutubeEmbedUrl = (videoId, autoplay = false, prefer1080 = false) =>
  `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1${autoplay ? "&autoplay=1" : ""}${prefer1080 ? "&vq=hd1080&hd=1" : ""}`;

const getYoutubeLoopBackgroundUrl = (videoId, prefer1080 = false) =>
  `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${videoId}&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3&disablekb=1${prefer1080 ? "&vq=hd1080&hd=1" : ""}`;

function HeroVideoOverlay({ videoId, title, onOpen, prefer1080 = false }) {
  return (
    <div className="cb-hero-video-showcase">
      <div className="cb-hero-video-shell">
        <div className="cb-hero-video-frame">
          <iframe
            src={getYoutubeLoopBackgroundUrl(videoId, prefer1080)}
            title={`${title} background preview`}
            loading="lazy"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            tabIndex={-1}
          />
          <div className="cb-hero-video-overlay" aria-hidden="true" />
          <button
            type="button"
            className="cb-hero-video-play"
            aria-label={`Play ${title}`}
            onClick={onOpen}
          >
            <span className="cb-hero-video-play-triangle" />
          </button>
          <button
            type="button"
            className="cb-hero-video-chip"
            aria-label={`Open ${title}`}
            onClick={onOpen}
          >
            Watch Demo
          </button>
        </div>
      </div>
    </div>
  );
}

const getLicenseKey = (license) =>
  String(
    license?.license_id ||
    license?.licenseId ||
    license?.id ||
    license?.license_number ||
    license?.licenseNumber ||
    `${license?.state || ""}-${license?.industry || ""}`
  );

const getDirectLicenseUrl = (license) => {
  if (!license) return "";
  const candidates = [license.doc_url, license.docUrl, license.supporting_document, license.supportingDocument];
  for (const candidate of candidates) {
    const value = String(candidate || "").trim();
    if (value.startsWith("http://") || value.startsWith("https://")) {
      return value;
    }
  }
  return "";
};

const hasLinkedDocument = (license) => {
  if (!license) return false;
  if (license.has_document === false || license.hasDocument === false) return false;
  if (license.has_document === true || license.hasDocument === true) return true;
  if (getDirectLicenseUrl(license)) return true;
  const storagePath = String(license.supporting_document || license.supportingDocument || "").trim();
  return storagePath.length > 0;
};

const buildPaginationItems = (current, total) => {
  if (total <= 1) return [];
  const items = [];
  const pushRange = (start, end) => {
    for (let i = start; i <= end; i += 1) {
      items.push(i);
    }
  };

  if (total <= 7) {
    pushRange(1, total);
    return items;
  }

  items.push(1);
  if (current <= 4) {
    pushRange(2, 5);
    items.push("dots");
    items.push(total);
    return items;
  }

  if (current >= total - 3) {
    items.push("dots");
    pushRange(Math.max(2, total - 4), total - 1);
    items.push(total);
    return items;
  }

  items.push("dots");
  pushRange(current - 1, current + 1);
  items.push("dots");
  items.push(total);
  return items;
};

const getDeviceType = () => {
  const ua = navigator.userAgent || "";
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ? "mobile" : "desktop";
};

const shuffleProfiles = (list) => {
  const items = [...list];
  for (let i = items.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items;
};

const CANONICAL_INDUSTRY_LABELS = {
  insurance: "Insurance",
  "real estate": "Real Estate",
  mortgage: "Mortgage",
  "financial services": "Financial Services",
};

const normalizeIndustryLabel = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  const key = trimmed.toLowerCase();
  return CANONICAL_INDUSTRY_LABELS[key] || trimmed;
};

const normalizeIndustryList = (list) => {
  const seen = new Set();
  const normalized = [];
  (list || []).forEach((item) => {
    const label = normalizeIndustryLabel(item);
    if (!label) return;
    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    normalized.push(label);
  });
  return normalized;
};

const normalizeCategoriesByIndustry = (raw) => {
  const normalized = {};
  Object.entries(raw || {}).forEach(([industry, categories]) => {
    const label = normalizeIndustryLabel(industry);
    if (!label) return;
    const list = Array.isArray(categories) ? categories : [];
    const existing = Array.isArray(normalized[label]) ? normalized[label] : [];
    normalized[label] = [...existing, ...list];
  });
  return normalized;
};

const groupResults = (items = []) => {
  const map = new Map();
  items.forEach((item, index) => {
    const emailDisplay = item.email_display || item.emailDisplay || "";
    const phoneDisplay = item.phone_display || item.phoneDisplay || "";
    const emailRaw = item.email || "";
    const phoneRaw = item.phone || "";
    const identifier = [
      item.public_slug || item.publicSlug || "",
      item.license_id || item.licenseId || "",
      item.first_name || "",
      item.last_name || "",
    ]
      .join("|")
      .toLowerCase();
    const key = item.public_slug || item.publicSlug || item.license_id || item.licenseId || identifier || `person-${index}`;
    if (!map.has(key)) {
      map.set(key, {
        id: key,
        firstName: item.first_name || "",
        lastName: item.last_name || "",
        email: emailRaw,
        phone: phoneRaw,
        emailDisplay,
        phoneDisplay,
        company: item.company_agency || "",
        profilePhoto: item.profile_photo || null,
        title: item.title || "",
        publicSlug: item.public_slug || "",
        businessCity: item.business_city || "",
        businessState: item.business_state || "",
        businessZip: item.business_zip || "",
        subscriptionTier: item.subscription_tier || item.subscriptionTier || "",
        subscriptionStatus: item.subscription_status || item.subscriptionStatus || "",
        licenses: [],
        industries: new Set(),
        states: new Set(),
        statuses: new Set(),
        hasVerified: false,
        hasUnverified: false,
        isSponsored: false,
      });
    }
    const group = map.get(key);
    group.licenses.push(item);
    if (item.industry) group.industries.add(item.industry);
    if (item.state) group.states.add(item.state);
    if (item.status) group.statuses.add(String(item.status));
    if (!group.title && item.title) group.title = item.title;
    if (!group.publicSlug && item.public_slug) group.publicSlug = item.public_slug;
    if (!group.businessCity && item.business_city) group.businessCity = item.business_city;
    if (!group.businessState && item.business_state) group.businessState = item.business_state;
    if (!group.businessZip && item.business_zip) group.businessZip = item.business_zip;
    if (!group.profilePhoto && item.profile_photo) group.profilePhoto = item.profile_photo;
    if (!group.email && emailRaw) group.email = emailRaw;
    if (!group.phone && phoneRaw) group.phone = phoneRaw;
    if (!group.emailDisplay && emailDisplay) group.emailDisplay = emailDisplay;
    if (!group.phoneDisplay && phoneDisplay) group.phoneDisplay = phoneDisplay;
    if (!group.company && item.company_agency) group.company = item.company_agency;
    if (!group.subscriptionTier && (item.subscription_tier || item.subscriptionTier)) {
      group.subscriptionTier = item.subscription_tier || item.subscriptionTier || "";
    }
    if (!group.subscriptionStatus && (item.subscription_status || item.subscriptionStatus)) {
      group.subscriptionStatus = item.subscription_status || item.subscriptionStatus || "";
    }
    if (item.verified) group.hasVerified = true;
    if (item.is_unverified) group.hasUnverified = true;
    if (item.is_sponsored) group.isSponsored = true;
  });
  return Array.from(map.values()).map((group) => ({
    ...group,
    licenses: sortLicensesByState(group.licenses),
    industries: Array.from(group.industries),
    states: sortStateCodes(Array.from(group.states)),
    statuses: Array.from(group.statuses),
  }));
};

export default function PublicFindPro({ embedded = false }) {
  const location = useLocation();
  const debugMode = useMemo(() => new URLSearchParams(location.search).get("debug") === "1", [location.search]);
  const isVerifyPage = location.pathname === "/";
  const isFindProPage =
    location.pathname.startsWith("/find-a-pro") || location.pathname.startsWith("/find-pro");
  const isPricingPage = location.pathname.startsWith("/pricing");
  const isAboutPage = location.pathname.startsWith("/about");
  const isPrivacyPage = location.pathname.startsWith("/privacy-policy");
  const isTermsPage = location.pathname.startsWith("/terms-of-service");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFloatingNav, setShowFloatingNav] = useState(true);
  const [heroVideoModalOpen, setHeroVideoModalOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filtersMounted, setFiltersMounted] = useState(false);
  const [locationMode, setLocationMode] = useState("state");
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [categoriesByIndustry, setCategoriesByIndustry] = useState(() => mergeCategoriesByIndustry({}));
  const [formValues, setFormValues] = useState({
    industry: "",
    category: "",
    state: "FL",
    zip: "",
    city: "",
  });
  const [filtersApplied, setFiltersApplied] = useState(DEFAULT_FILTERS);
  const [filtersDraft, setFiltersDraft] = useState(DEFAULT_FILTERS);
  const [results, setResults] = useState([]);
  const [sponsoredResults, setSponsoredResults] = useState([]);
  const [featuredIds, setFeaturedIds] = useState(() => new Set());
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [debugInfo, setDebugInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [openTooltipFor, setOpenTooltipFor] = useState(null);
  const filterCloseTimerRef = React.useRef(null);
  const filterModalRef = useRef(null);
  const filterCloseButtonRef = useRef(null);
  const searchIntentRef = useRef(false);
  const mobileDrawerRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);
  const lastScrollYRef = useRef(0);
  const landingRootRef = useRef(null);
  const heroVideoModalRef = useRef(null);
  const heroVideoCloseRef = useRef(null);
  const profileModalRef = useRef(null);
  const profileCloseButtonRef = useRef(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [pendingPayload, setPendingPayload] = useState(null);
  const [gateState, setGateState] = useState(null); // null | "captcha" | "fetching"
  const handleTurnstileError = useCallback((code) => {
    setCaptchaOpen(false);
    setPendingPayload(null);
    setGateState(null);
    setTurnstileToken("");
    setError(getTurnstileErrorMessage(code));
    if (searchIntentRef.current) {
      searchIntentRef.current = false;
    }
  }, []);
  const [profilePanel, setProfilePanel] = useState("details"); // details | contact
  const [contactStep, setContactStep] = useState("form"); // form | thanks
  const [contactLeadName, setContactLeadName] = useState("");
  const [contactLeadEmail, setContactLeadEmail] = useState("");
  const [contactLeadPhone, setContactLeadPhone] = useState("");
  const [contactLeadNotes, setContactLeadNotes] = useState("");
  const [submittingContactLead, setSubmittingContactLead] = useState(false);
  const [licenseDocStatus, setLicenseDocStatus] = useState({});
  const [licenseDocUrls, setLicenseDocUrls] = useState({});
  const canContactPro = useMemo(() => {
    const tier = String(selectedProfile?.subscriptionTier || "").toLowerCase();
    const status = String(selectedProfile?.subscriptionStatus || "").toLowerCase();
    if (tier === "pro" || tier === "elite") return true;
    return status === "pro" || status === "elite" || status === "active" || status === "trialing";
  }, [selectedProfile]);
  const contactEnabled = !!selectedProfile?.hasVerified && canContactPro;

  const resetContactPanel = useCallback(() => {
    setProfilePanel("details");
    setContactStep("form");
    setContactLeadName("");
    setContactLeadEmail("");
    setContactLeadPhone("");
    setContactLeadNotes("");
    setSubmittingContactLead(false);
  }, []);

  useEffect(() => {
    if (!selectedProfile) return;
    if (contactEnabled) return;
    if (profilePanel === "contact") {
      setProfilePanel("details");
      setContactStep("form");
    }
  }, [selectedProfile, contactEnabled, profilePanel]);

  useEffect(() => {
    const loadProfileEnhancements = async () => {
      if (!selectedProfile) return;
      const shouldLookup =
        !selectedProfile.profilePhoto ||
        (!selectedProfile.publicSlug && selectedProfile.hasVerified) ||
        (!selectedProfile.company && selectedProfile.hasVerified) ||
        (!selectedProfile.title && selectedProfile.hasVerified);

      const slug = selectedProfile.publicSlug;
      if (!slug) return;

      const updates = {};
      if (shouldLookup) {
        try {
          const query = supabase
            .from("profiles")
            .select("public_slug,company_agency,title")
            .eq("public_slug", slug)
            .limit(1);
          const { data } = await query.maybeSingle();
          if (data) {
            if (!selectedProfile.publicSlug && data.public_slug) updates.publicSlug = data.public_slug;
            if (!selectedProfile.company && data.company_agency) updates.company = data.company_agency;
            if (!selectedProfile.title && data.title) updates.title = data.title;
          }
        } catch (_err) {
          // ignore
        }
      }

      const current = String(selectedProfile.profilePhoto || "").trim();
      if (!current || !current.startsWith("http")) {
        try {
          const res = await fetch(`${API_BASE}/api/profile-photo/${encodeURIComponent(slug)}`);
          if (res.ok) {
            const data = await res.json();
            const url = String(data?.url || "").trim();
            if (url) updates.profilePhoto = url;
          }
        } catch (_err) {
          // ignore
        }
      }

      if (Object.keys(updates).length > 0) {
        setSelectedProfile((prev) => (prev ? { ...prev, ...updates } : prev));
      }
    };
    loadProfileEnhancements();
  }, [selectedProfile]);

  const openFilters = useCallback(() => {
    setFiltersDraft(filtersApplied);
    if (filterCloseTimerRef.current) {
      clearTimeout(filterCloseTimerRef.current);
      filterCloseTimerRef.current = null;
    }
    if (!filtersMounted) {
      setFiltersMounted(true);
      requestAnimationFrame(() => setFiltersOpen(true));
      return;
    }
    setFiltersOpen(true);
  }, [filtersApplied, filtersMounted]);

  const closeFilters = useCallback(() => {
    setFiltersOpen(false);
    if (filterCloseTimerRef.current) clearTimeout(filterCloseTimerRef.current);
    filterCloseTimerRef.current = setTimeout(() => {
      setFiltersMounted(false);
      filterCloseTimerRef.current = null;
    }, FILTER_ANIMATION_MS);
  }, []);

  const cancelFilters = useCallback(() => {
    setFiltersDraft(filtersApplied);
    closeFilters();
  }, [closeFilters, filtersApplied]);

  const applyFiltersAndClose = useCallback(() => {
    setFiltersApplied(filtersDraft);
    closeFilters();
  }, [closeFilters, filtersDraft]);

  useEffect(() => {
    return () => {
      if (filterCloseTimerRef.current) clearTimeout(filterCloseTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!openTooltipFor && !filtersOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpenTooltipFor(null);
        cancelFilters();
      }
    };
    const onClick = (e) => {
      const target = e.target;
      if (!target?.closest?.(".info-tooltip-wrap") && !target?.closest?.(".filter-dropdown-wrap")) {
        setOpenTooltipFor(null);
      }
      if (!target?.closest?.(".filter-dropdown-wrap")) {
        cancelFilters();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("click", onClick);
    };
  }, [cancelFilters, openTooltipFor, filtersOpen]);

  useEffect(() => {
    const loadOptions = async () => {
      setOptionsLoading(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL || ""}/api/pro-options`);
        if (!res.ok) return;
        const data = await res.json();
        const industries = Array.isArray(data?.industries) ? data.industries : [];
        const catsByIndustry = data?.categoriesByIndustry && typeof data.categoriesByIndustry === "object"
          ? data.categoriesByIndustry
          : {};
        const normalizedCats = normalizeCategoriesByIndustry(catsByIndustry);
        const mergedCats = mergeCategoriesByIndustry(normalizedCats);
        const normalizedIndustries = normalizeIndustryList(industries);
        const mergedIndustries = normalizeIndustryList([
          ...normalizedIndustries,
          ...Object.keys(mergedCats),
        ]);
        mergedIndustries.sort((a, b) => a.localeCompare(b));
        setIndustryOptions(mergedIndustries);
        setCategoriesByIndustry(mergedCats);
      } catch (_err) {
        // ignore
      } finally {
        setOptionsLoading(false);
      }
    };
    loadOptions();
  }, []);

  useEffect(() => {
    const validCategories = Array.isArray(categoriesByIndustry[formValues.industry])
      ? categoriesByIndustry[formValues.industry]
      : [];
    if (formValues.category && !validCategories.includes(formValues.category)) {
      setFormValues((prev) => ({ ...prev, category: "" }));
    }
  }, [categoriesByIndustry, formValues.category, formValues.industry]);

  const grouped = useMemo(() => groupResults(results), [results]);

  const filtered = useMemo(() => {
    const status = (filtersApplied.licenseStatus || "any").toLowerCase();
    return grouped.filter((person) => {
      if (filtersApplied.verifiedOnly && !person.hasVerified) return false;
      if (status !== "any") {
        const matches = person.licenses.some((lic) => String(lic.status || "").toLowerCase() === status);
        if (!matches) return false;
      }
      return true;
    });
  }, [grouped, filtersApplied]);

  useEffect(() => {
    const pool = filtered.filter((p) => p.isSponsored);
    if (pool.length === 0) {
      setSponsoredResults([]);
      setFeaturedIds(new Set());
      return;
    }
    const shuffled = shuffleProfiles(pool);
    const featured = shuffled.slice(0, SPONSORED_SLOTS);
    setSponsoredResults(featured);
    setFeaturedIds(new Set(featured.map((person) => person.id)));
  }, [filtered]);

  const organicResults = useMemo(
    () => filtered.filter((p) => !featuredIds.has(p.id)),
    [filtered, featuredIds]
  );
  const totalPages = Math.max(1, Math.ceil(organicResults.length / pageSize));
  const paginationItems = useMemo(
    () => buildPaginationItems(currentPage, totalPages),
    [currentPage, totalPages]
  );
  const paginatedOrganicResults = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return organicResults.slice(start, start + pageSize);
  }, [currentPage, organicResults, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filtered]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const performSearch = async (payload) => {
    setError("");
    setLoading(true);
    let didSucceed = false;
    try {
      const token = String(turnstileToken || "").trim();
      const res = await fetch(`${process.env.REACT_APP_API_URL || ""}/api/pro-search`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { "x-turnstile-token": token } : {}) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        let message = text;
        try {
          const parsed = JSON.parse(text);
          message = parsed?.error || text;
        } catch (_err) {
          // ignore
        }
        if (res.status === 400 || res.status === 403) {
          if (String(message || "").toLowerCase().includes("captcha")) {
            setTurnstileToken("");
            setCaptchaOpen(true);
            setGateState("captcha");
            setPendingPayload(payload);
            setError("");
            return;
          }
        }
        throw new Error(message || "Search failed");
      }
      const data = await res.json();
      setResults(Array.isArray(data?.results) ? data.results : []);
      setDebugInfo(data?.debug || null);
      didSucceed = true;
      if (searchIntentRef.current) {
        const params = {
          context: embedded ? "internal" : "public",
          location_mode: locationMode || "state",
          license_status: String(payload?.licenseStatus || "any").toLowerCase(),
          verified_only: payload?.verifiedOnly ? "true" : "false",
        };
        trackGoal("pros_searched", params);
        searchIntentRef.current = false;
      }
      setGateState(null);
      if (debugMode) {
        // eslint-disable-next-line no-console
        console.log("[Find a Pro debug] response", data?.debug || { results: (data?.results || []).length });
      }
    } catch (err) {
      setError(err.message || "Unable to search right now.");
      setResults([]);
      setDebugInfo(null);
      if (!String(err?.message || "").toLowerCase().includes("captcha")) {
        setGateState(null);
      }
    } finally {
      if (!didSucceed && searchIntentRef.current) {
        searchIntentRef.current = false;
      }
      setTurnstileToken("");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!captchaOpen) return;
    if (!pendingPayload) return;
    if (!String(turnstileToken || "").trim()) return;
    const payload = pendingPayload;
    setPendingPayload(null);
    setCaptchaOpen(false);
    setGateState("fetching");
    performSearch(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captchaOpen, pendingPayload, turnstileToken]);

  useEffect(() => {
    if (gateState !== "captcha") return;
    const timer = setTimeout(() => {
      if (String(turnstileToken || "").trim()) return;
      handleTurnstileError("timeout");
    }, TURNSTILE_GATE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [gateState, turnstileToken, handleTurnstileError]);

  const initialsFrom = (first, last) => {
    const f = first?.[0] || "";
    const l = last?.[0] || "";
    return (f + l).toUpperCase() || "U";
  };

  const resolveLicenseUrl = async (license) => {
    if (!license) return "";
    const direct = getDirectLicenseUrl(license);
    if (direct) return direct;
    if (!hasLinkedDocument(license)) return "";
    const licenseId = license.license_id || license.licenseId || license.id;
    if (!licenseId) return "";
    if (!license.verified) return "";
    try {
      const state = String(license.state || "").trim();
      const query = state ? `?state=${encodeURIComponent(state)}` : "";
      const res = await fetch(`${API_BASE}/api/license-doc/${encodeURIComponent(String(licenseId))}${query}`);
      if (!res.ok) return "";
      const data = await res.json();
      return data?.url || "";
    } catch (_err) {
      return "";
    }
  };

  const openProfile = (person) => {
    resetContactPanel();
    setLicenseDocStatus({});
    setLicenseDocUrls({});
    setSelectedProfile(person);
    if (featuredIds.has(person.id)) {
      const profileId = person.publicSlug || person.id || "";
      if (profileId) {
        trackGoal("featured_profile_clicked", { profile_id: profileId });
      }
    }
    logViewLead("profile", null, person);
  };

  const shouldIgnoreCardClick = (event) => {
    const interactive = event.target?.closest?.("button, a, input, select, textarea, [role='button']");
    return !!interactive && interactive !== event.currentTarget;
  };

  const handleResultCardClick = (event, person) => {
    if (shouldIgnoreCardClick(event)) return;
    openProfile(person);
  };

  const handleResultCardKeyDown = (event, person) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    if (shouldIgnoreCardClick(event)) return;
    event.preventDefault();
    openProfile(person);
  };

  const closeProfile = () => {
    resetContactPanel();
    setLicenseDocStatus({});
    setLicenseDocUrls({});
    setSelectedProfile(null);
  };
  const closeHeroVideoModal = useCallback(() => setHeroVideoModalOpen(false), []);

  useFocusTrap({
    isOpen: !embedded && mobileMenuOpen,
    containerRef: mobileDrawerRef,
    onClose: () => setMobileMenuOpen(false),
    returnFocusRef: mobileMenuButtonRef,
    lockScroll: true,
  });

  useFocusTrap({
    isOpen: filtersMounted && filtersOpen,
    containerRef: filterModalRef,
    onClose: cancelFilters,
    initialFocusRef: filterCloseButtonRef,
    lockScroll: true,
  });

  useFocusTrap({
    isOpen: !!selectedProfile,
    containerRef: profileModalRef,
    onClose: closeProfile,
    initialFocusRef: profileCloseButtonRef,
    lockScroll: true,
  });

  useFocusTrap({
    isOpen: heroVideoModalOpen,
    containerRef: heroVideoModalRef,
    onClose: closeHeroVideoModal,
    initialFocusRef: heroVideoCloseRef,
    lockScroll: true,
  });

  useEffect(() => {
    const rootNode = landingRootRef.current;
    if (!rootNode) return undefined;
    const revealNodes = rootNode.querySelectorAll("[data-reveal]");
    if (!revealNodes.length) return undefined;

    if (embedded) {
      revealNodes.forEach((node) => node.classList.add("is-visible"));
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      revealNodes.forEach((node) => node.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.16, rootMargin: "0px 0px -10% 0px" }
    );

    revealNodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [embedded, location.pathname]);

  useEffect(() => {
    if (!selectedProfile) return;
    let cancelled = false;
    const loadDocStatus = () => {
      const licenses = sortLicensesByState(selectedProfile.licenses);
      const statusMap = {};
      const urlMap = {};
      licenses.forEach((lic) => {
        if (!lic?.verified) return;
        const licenseKey = getLicenseKey(lic);
        const direct = getDirectLicenseUrl(lic);
        if (direct) {
          statusMap[licenseKey] = "available";
          urlMap[licenseKey] = direct;
          return;
        }
        if (!hasLinkedDocument(lic)) {
          statusMap[licenseKey] = "missing";
        }
      });
      if (cancelled) return;
      setLicenseDocStatus(statusMap);
      setLicenseDocUrls(urlMap);
    };
    loadDocStatus();
    return () => {
      cancelled = true;
    };
  }, [selectedProfile]);

  useEffect(() => {
    if (embedded) return undefined;
    lastScrollYRef.current = window.scrollY || 0;

    const onScroll = () => {
      const currentY = window.scrollY || 0;
      const previousY = lastScrollYRef.current;
      const delta = currentY - previousY;
      if (Math.abs(delta) < 6) return;

      if (currentY <= 24) {
        setShowFloatingNav(true);
      } else if (delta > 0) {
        setShowFloatingNav(false);
      } else {
        setShowFloatingNav(true);
      }

      lastScrollYRef.current = currentY;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [embedded]);

  useEffect(() => {
    if (mobileMenuOpen) setShowFloatingNav(true);
  }, [mobileMenuOpen]);

  const handleLicenseOpen = async (license, licenseKey) => {
    await logViewLead("license", license);
    const cachedUrl = licenseDocUrls[licenseKey];
    if (cachedUrl) {
      window.open(cachedUrl, "_blank", "noopener,noreferrer");
      return;
    }
    const link = await resolveLicenseUrl(license);
    if (link) {
      window.open(link, "_blank", "noopener,noreferrer");
      setLicenseDocStatus((prev) => ({ ...prev, [licenseKey]: "available" }));
      setLicenseDocUrls((prev) => ({ ...prev, [licenseKey]: link }));
      return;
    }
    setLicenseDocStatus((prev) => ({ ...prev, [licenseKey]: "missing" }));
  };

  const formatPhoneInput = (val) => {
    const digits = String(val || "").replace(/\D/g, "").slice(0, 10);
    const parts = [];
    if (digits.length > 0) parts.push(digits.slice(0, 3));
    if (digits.length >= 4) parts.push(digits.slice(3, 6));
    if (digits.length >= 7) parts.push(digits.slice(6, 10));
    return parts.length === 0 ? "" : parts.length === 1 ? parts[0] : `(${parts[0]}) ${parts[1]}${parts[2] ? `-${parts[2]}` : ""}`;
  };

  const logLeadEvent = async (type, profile) => {
    const slug = profile?.publicSlug || "";
    if (!slug) return;
    try {
      await fetch(`${API_BASE}/api/lead-event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ publicSlug: slug, type, deviceType: getDeviceType() }),
      });
    } catch (_err) {
      // no-op
    }
  };

  const isUuid = (value) =>
    typeof value === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

  const buildLicenseViewLabel = (license) => {
    if (!license) return "License";
    const industry = license.industry || "";
    const state = license.state || "";
    const number = license.license_number || license.licenseNumber || "";
    const parts = [industry, state].filter(Boolean).join(" - ");
    const numberLabel = number ? ` #${number}` : "";
    return parts ? `${parts}${numberLabel}` : "License";
  };

  const logViewLead = async (viewType, license, profile) => {
    const slug = profile?.publicSlug || selectedProfile?.publicSlug || "";
    if (!slug) return;
    const viewTargetType = String(viewType || "").toLowerCase() || "profile";
    const viewTargetLabel = viewTargetType === "license" ? buildLicenseViewLabel(license) : "Profile";
    const viewTargetId =
      viewTargetType === "license"
        ? license?.license_id || license?.licenseId || license?.id || null
        : null;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token || "";
      await fetch(`${API_BASE}/api/leads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          publicSlug: slug,
          leadType: viewTargetType === "license" ? "license_view" : "profile_view",
          viewTargetType,
          viewTargetLabel,
          ...(isUuid(viewTargetId) ? { viewTargetId } : {}),
          deviceType: getDeviceType(),
        }),
      });
    } catch (_err) {
      // no-op
    }
  };

  const submitContactLead = async (profile) => {
    const slug = profile?.publicSlug || "";
    if (!slug) return;
    setSubmittingContactLead(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token || "";
      const res = await fetch(`${API_BASE}/api/leads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          publicSlug: slug,
          leadType: "inquiry",
          name: contactLeadName.trim(),
          email: contactLeadEmail.trim(),
          phone: contactLeadPhone.trim(),
          inquiryDetails: contactLeadNotes.trim(),
          notes: contactLeadNotes.trim(),
          deviceType: getDeviceType(),
        }),
      });
      if (res.ok) {
        trackGoal("lead_generated", { profile_id: slug, source: "search" });
      }
    } catch (_err) {
      // allow continue even if logging fails
    } finally {
      setSubmittingContactLead(false);
    }
  };

  const renderVerificationPill = (person, tooltipKey) => {
    const isVerified = person.hasVerified;
    const pillKey = `pill-${tooltipKey}`;
    return (
      <span className="info-tooltip-wrap pill-tooltip-wrap">
        <button
          type="button"
          className={`pill-tooltip-btn ${isVerified ? "license-verify-badge vettify-pill-badge" : "license-unverified-badge"}`}
          aria-label={isVerified ? "Verified" : "Not Verified"}
          aria-expanded={openTooltipFor === pillKey}
          onClick={(e) => {
            e.stopPropagation();
            setOpenTooltipFor((prev) => (prev === pillKey ? null : pillKey));
          }}
        >
          <span className="vettify-pill__icon small-dot">
            <img src={clearBadgeMarkWhite} alt="" />
          </span>
          <span className="vettify-pill__text">{isVerified ? "Verified" : "Not Verified"}</span>
        </button>
        <span role="tooltip" className={`info-tooltip ${openTooltipFor === pillKey ? "open" : ""}`}>
          {isVerified ? VERIFICATION_TOOLTIP_COPY : UNVERIFIED_TOOLTIP_COPY}
        </span>
      </span>
    );
  };

  const renderPersonCard = (person, { tooltipKey, sponsored } = {}) => (
    <article
      key={person.id}
      className={`person-card clickable-result-card${person.hasUnverified && !person.hasVerified ? " unverified-card" : ""}${sponsored ? " sponsored-card" : ""
        }`}
      tabIndex={0}
      role="button"
      aria-label={`Open profile for ${(person.firstName || person.lastName) ? `${person.firstName || ""} ${person.lastName || ""}`.trim() : "this result"}`}
      onClick={(event) => handleResultCardClick(event, person)}
      onKeyDown={(event) => handleResultCardKeyDown(event, person)}
    >
      <div className="person-header">
        <div>
          <h4>
            {(person.firstName || person.lastName)
              ? `${person.firstName || ""} ${person.lastName || ""}`.trim()
              : "Name unavailable"}
          </h4>
          {person.industries.length > 0 && (
            <div className="industry-pills">
              {person.industries.slice(0, 3).map((industry) => (
                <span key={industry} className="industry-pill">
                  {industry}
                </span>
              ))}
              {person.industries.length > 3 && (
                <span className="industry-pill muted-pill">+{person.industries.length - 3}</span>
              )}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {sponsored && <div className="sponsored-badge">Sponsored</div>}
          {renderVerificationPill(person, tooltipKey)}
        </div>
      </div>

      <div className="person-meta">
        {(person.email || person.emailDisplay) && (
          <span>
            <EmailIcon className="card-line-icon" />
            {person.email || person.emailDisplay}
          </span>
        )}
        {(person.phone || person.phoneDisplay) && (
          <span>
            <PhoneIcon className="card-line-icon" />
            {person.phone || person.phoneDisplay}
          </span>
        )}
        {person.company && (
          <span>
            <CompanyIcon className="card-line-icon" />
            {person.company}
          </span>
        )}
        {person.states.length > 0 && (
          <span>
            <LicenseIcon className="card-line-icon" />
            {person.states.join(", ")}
          </span>
        )}
      </div>

      {person.hasUnverified && !person.hasVerified && (
        <p className="unverified-disclaimer">
          This profile is automatically generated from publicly available state licensing databases. This individual
          has not claimed or verified this information with ClearBadge.
        </p>
      )}

      <button type="button" className="view-link view-link-btn" onClick={() => openProfile(person)}>
        {person.hasUnverified && !person.hasVerified ? "View details ->" : "View licenses ->"}
      </button>
      {sponsored && (
        <span className="info-tooltip-wrap featured-tooltip">
          <button
            type="button"
            className="sponsored-tag"
            aria-label="About featured professionals"
            aria-expanded={openTooltipFor === `featured-${person.id}`}
            onClick={(e) => {
              e.stopPropagation();
              setOpenTooltipFor((prev) => (prev === `featured-${person.id}` ? null : `featured-${person.id}`));
            }}
          >
            Featured Professional
            <InfoIcon className="info-icon featured-icon" />
          </button>
          <span
            role="tooltip"
            className={`info-tooltip ${openTooltipFor === `featured-${person.id}` ? "open" : ""}`}
          >
            {FEATURED_TOOLTIP_COPY}
          </span>
        </span>
      )}
    </article>
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    setHasSearched(true);
    searchIntentRef.current = true;
    if (debugMode) {
      // eslint-disable-next-line no-console
      console.log("[Find a Pro debug] request", {
        industry: (formValues.industry || "").trim(),
        category: (formValues.category || "").trim(),
        state: formValues.state,
        zip: locationMode === "zip" ? (formValues.zip || "").trim() : "",
        city: locationMode === "city" ? (formValues.city || "").trim() : "",
        verifiedOnly: !!filtersApplied.verifiedOnly,
        licenseStatus: filtersApplied.licenseStatus,
      });
    }
    const payload = {
      industry: (formValues.industry || "").trim(),
      category: (formValues.category || "").trim(),
      state: formValues.state,
      zip: locationMode === "zip" ? (formValues.zip || "").trim() : "",
      city: locationMode === "city" ? (formValues.city || "").trim() : "",
      verifiedOnly: !!filtersApplied.verifiedOnly,
      licenseStatus: filtersApplied.licenseStatus,
      debug: debugMode,
    };
    if (TURNSTILE_SITE_KEY && !String(turnstileToken || "").trim()) {
      setPendingPayload(payload);
      setCaptchaOpen(true);
      setGateState("captcha");
      return;
    }
    if (TURNSTILE_SITE_KEY) setGateState("fetching");
    performSearch(payload);
  };

  const showResultsPanel = loading || !!error || hasSearched;

  return (
    <div
      ref={landingRootRef}
      className={`public-shell ${embedded ? "embedded landing find-pro-page clearbadge-public" : "landing find-pro-page clearbadge-public"}`}
    >
      {!embedded && (
        <header className={`public-topbar landing-topbar ${showFloatingNav ? "" : "landing-topbar--hidden"}`}>
          <div className="landing-topbar-inner">
            <div className="landing-topbar-left">
              <Link to="/" className="brand-inline" aria-label="ClearBadge home">
                <img src={LOGO_WHITE_PUBLIC_PATH} alt="ClearBadge logo" className="brand-logo" />
              </Link>
              <nav className="public-search-nav" aria-label="Search navigation">
                <Link
                  className={`top-link ${location.pathname === "/" ? "active" : ""}`}
                  to="/"
                  data-reveal
                  style={{ "--reveal-delay": "560ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
                >
                  Verify a License
                </Link>
                <Link
                  className={`top-link ${location.pathname.startsWith("/find-a-pro") ? "active" : ""}`}
                  to="/find-a-pro"
                  data-reveal
                  style={{ "--reveal-delay": "700ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
                >
                  Find a Pro
                </Link>
                <Link
                  className={`top-link ${location.pathname.startsWith("/pricing") ? "active" : ""}`}
                  to="/pricing"
                  data-reveal
                  style={{ "--reveal-delay": "840ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
                >
                  Pricing
                </Link>
                <Link
                  className={`top-link ${location.pathname.startsWith("/about") ? "active" : ""}`}
                  to="/about"
                  data-reveal
                  style={{ "--reveal-delay": "980ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
                >
                  About
                </Link>
              </nav>
            </div>
            <div className="public-links landing-topbar-right">
              <Link
                className="top-link"
                to="/login"
                data-reveal
                style={{ "--reveal-delay": "1120ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                Sign In
              </Link>
              <Link
                className="primary nav-cta mobile-signup"
                to="/login?mode=signup"
                data-reveal
                style={{ "--reveal-delay": "1260ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                Get Verified
              </Link>
              <button
                ref={mobileMenuButtonRef}
                className="icon-btn mobile-menu-btn"
                type="button"
                aria-label="Toggle menu"
                aria-expanded={mobileMenuOpen}
                aria-controls="public-find-pro-mobile-menu"
                onClick={() => setMobileMenuOpen((open) => !open)}
              >
                <span className="burger-line" aria-hidden="true" />
              </button>
            </div>
          </div>
        </header>
      )}

      {!embedded && (
        <div className={`public-mobile-overlay ${mobileMenuOpen ? "open" : ""}`}>
          <button
            type="button"
            className="public-mobile-overlay-dismiss"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          />
          <div
            id="public-find-pro-mobile-menu"
            ref={mobileDrawerRef}
            className={`public-mobile-drawer ${mobileMenuOpen ? "open" : ""}`}
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="public-mobile-header">
              <Link to="/" className="brand-inline" aria-label="ClearBadge home" onClick={() => setMobileMenuOpen(false)}>
                <img src={LOGO_WHITE_PUBLIC_PATH} alt="ClearBadge logo" className="brand-logo" />
              </Link>
              <button className="icon-btn mobile-drawer-close" type="button" aria-label="Close menu" onClick={() => setMobileMenuOpen(false)}>
                <span className="mobile-drawer-close-icon" aria-hidden="true" />
              </button>
            </div>
            <div className="public-mobile-links">
              <Link to="/login" className="public-mobile-link" onClick={() => setMobileMenuOpen(false)}>
                Sign In
              </Link>
              <Link to="/login?mode=signup" className="public-mobile-link primary" onClick={() => setMobileMenuOpen(false)}>
                Get Verified
              </Link>
              <div className="mobile-menu-section">
                <span className="mobile-menu-label">TOOLS</span>
                <Link
                  to="/"
                  className={`public-mobile-link secondary ${isVerifyPage ? "active" : ""}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="mobile-link-text">Verify a License</span>
                </Link>
                <Link
                  to="/find-a-pro"
                  className={`public-mobile-link secondary ${isFindProPage ? "active" : ""}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="mobile-link-text">Find a Pro</span>
                </Link>
              </div>
              <div className="mobile-menu-section">
                <span className="mobile-menu-label">RESOURCES</span>
                <Link
                  to="/about"
                  className={`public-mobile-link secondary ${isAboutPage ? "active" : ""}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="mobile-link-text">About</span>
                </Link>
                <Link
                  to="/pricing"
                  className={`public-mobile-link secondary ${isPricingPage ? "active" : ""}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="mobile-link-text">Pricing</span>
                </Link>
                <Link
                  to="/privacy-policy"
                  className={`public-mobile-link secondary ${isPrivacyPage ? "active" : ""}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="mobile-link-text">Privacy Policy</span>
                </Link>
                <Link
                  to="/terms-of-service"
                  className={`public-mobile-link secondary ${isTermsPage ? "active" : ""}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="mobile-link-text">Terms of Service</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="landing-main" id={embedded ? undefined : "main-content"}>
        <section className={`hero landing-hero${embedded ? " embedded-hero" : ""}`}>
          <div className="hero-inner">
            {!embedded && (
              <div className="trust-pill" data-reveal style={{ "--reveal-delay": "1420ms" }}>
                <VerificationIcon className="pill-icon" /> Search for professionals near you
              </div>
            )}
            <h1 data-reveal style={{ "--reveal-delay": "1580ms" }}>Find a Pro</h1>
            <p className="hero-lead" data-reveal style={{ "--reveal-delay": "1740ms" }}>
              Search by industry and state to discover professionals and view verification status.
            </p>

            <div className="search-card" data-reveal style={{ "--reveal-delay": "1900ms" }}>
              <div className="search-card-top">
                <div className="pill-toggle" aria-label="Location mode">
                  <button
                    type="button"
                    className={locationMode === "state" ? "active" : ""}
                    onClick={() => setLocationMode("state")}
                  >
                    State
                  </button>
                  <button
                    type="button"
                    className={locationMode === "zip" ? "active" : ""}
                    onClick={() => setLocationMode("zip")}
                  >
                    ZIP
                  </button>
                  <button
                    type="button"
                    className={locationMode === "city" ? "active" : ""}
                    onClick={() => setLocationMode("city")}
                  >
                    City
                  </button>
                </div>

                <div className="filter-dropdown-wrap">
                  <button
                    type="button"
                    className={`filter-icon-btn ${filtersOpen ? "open" : ""}${filtersApplied.verifiedOnly || filtersApplied.licenseStatus !== "any" ? " active" : ""
                      }`}
                    aria-label="Filter results"
                    aria-haspopup="dialog"
                    aria-expanded={filtersOpen}
                    onClick={() => (filtersOpen ? cancelFilters() : openFilters())}
                  >
                    <FilterIcon className="filter-icon" />
                  </button>
                  {filtersMounted && (
                    <div
                      className={`filter-overlay ${filtersOpen ? "open" : ""}`}
                      role="presentation"
                      onClick={cancelFilters}
                    >
                      <div
                        ref={filterModalRef}
                        className={`filter-modal ${filtersOpen ? "open" : ""}`}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Filters"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="filter-modal-header">
                          <button
                            type="button"
                            className="filter-modal-clear"
                            onClick={() => setFiltersDraft(DEFAULT_FILTERS)}
                          >
                            Clear
                          </button>
                          <div className="filter-modal-title">Filters</div>
                          <button
                            ref={filterCloseButtonRef}
                            type="button"
                            className="filter-modal-close"
                            aria-label="Close filters"
                            onClick={cancelFilters}
                          >
                            x
                          </button>
                        </div>

                        <div className="filter-modal-body">
                          <div className="filter-section">
                            <div className="filter-section-title">Verification</div>
                            <label className="filter-check-row">
                              <input
                                type="checkbox"
                                checked={filtersDraft.verifiedOnly}
                                onChange={(e) =>
                                  setFiltersDraft((prev) => ({ ...prev, verifiedOnly: e.target.checked }))
                                }
                              />
                              <span>ClearBadge Verified</span>
                            </label>
                          </div>

                          <div className="filter-divider" aria-hidden="true" />

                          <div className="filter-section">
                            <div className="filter-section-title">License Status</div>
                            <div className="filter-select-row">
                              <select
                                aria-label="License status filter"
                                value={filtersDraft.licenseStatus}
                                onChange={(e) =>
                                  setFiltersDraft((prev) => ({ ...prev, licenseStatus: e.target.value }))
                                }
                              >
                                {LICENSE_STATUS_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>

                        <div className="filter-modal-footer">
                          <button
                            type="button"
                            className="filter-reset-btn"
                            onClick={() => setFiltersDraft(DEFAULT_FILTERS)}
                          >
                            Reset
                          </button>
                          <button type="button" className="primary filter-done-btn" onClick={applyFiltersAndClose}>
                            Done
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <form className="search-form" onSubmit={handleSubmit}>
                <div className="form-row triple">
                  <div>
                    <label htmlFor="public-find-pro-industry">Industry</label>
                    <select
                      id="public-find-pro-industry"
                      aria-label="Industry"
                      value={formValues.industry}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, industry: e.target.value }))}
                      required
                    >
                      <option value="" disabled>
                        {optionsLoading ? "Loading..." : "Select industry"}
                      </option>
                      {industryOptions.length > 0
                        ? industryOptions.map((industry) => (
                          <option key={industry} value={industry}>
                            {industry}
                          </option>
                        ))
                        : [
                          "Healthcare",
                          "Real Estate",
                          "Insurance",
                          "Construction",
                          "Legal",
                          "Education",
                          "Finance",
                          "Engineering",
                        ].map((industry) => (
                          <option key={industry} value={industry}>
                            {industry}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="public-find-pro-category">Category</label>
                    <select
                      id="public-find-pro-category"
                      aria-label="Category"
                      value={formValues.category}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, category: e.target.value }))}
                      disabled={!formValues.industry}
                    >
                      <option value="">
                        {formValues.industry ? "All categories" : "Select an industry first"}
                      </option>
                      {(Array.isArray(categoriesByIndustry[formValues.industry])
                        ? categoriesByIndustry[formValues.industry]
                        : []
                      ).map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>
                  {locationMode === "state" && (
                    <div>
                      <label htmlFor="public-find-pro-state">State</label>
                      <select
                        id="public-find-pro-state"
                        aria-label="State"
                        autoComplete="address-level1"
                        value={formValues.state}
                        onChange={(e) => setFormValues((prev) => ({ ...prev, state: e.target.value }))}
                        required
                      >
                        {STATES.map((st) => (
                          <option key={st} value={st}>
                            {st}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {locationMode === "zip" && (
                    <div>
                      <label htmlFor="public-find-pro-zip">ZIP</label>
                      <div className="inline-field">
                        <select
                          id="public-find-pro-zip-state"
                          aria-label="State"
                          autoComplete="address-level1"
                          value={formValues.state}
                          onChange={(e) => setFormValues((prev) => ({ ...prev, state: e.target.value }))}
                          required
                        >
                          {STATES.map((st) => (
                            <option key={st} value={st}>
                              {st}
                            </option>
                          ))}
                        </select>
                        <input
                          id="public-find-pro-zip"
                          type="text"
                          inputMode="numeric"
                          autoComplete="postal-code"
                          aria-label="ZIP code"
                          placeholder="e.g. 33101"
                          value={formValues.zip}
                          onChange={(e) => setFormValues((prev) => ({ ...prev, zip: e.target.value }))}
                        />
                      </div>
                    </div>
                  )}

                  {locationMode === "city" && (
                    <div>
                      <label htmlFor="public-find-pro-city">City</label>
                      <div className="inline-field">
                        <select
                          id="public-find-pro-city-state"
                          aria-label="State"
                          autoComplete="address-level1"
                          value={formValues.state}
                          onChange={(e) => setFormValues((prev) => ({ ...prev, state: e.target.value }))}
                          required
                        >
                          {STATES.map((st) => (
                            <option key={st} value={st}>
                              {st}
                            </option>
                          ))}
                        </select>
                        <input
                          id="public-find-pro-city"
                          type="text"
                          autoComplete="address-level2"
                          aria-label="City"
                          placeholder="e.g. Miami"
                          value={formValues.city}
                          onChange={(e) => setFormValues((prev) => ({ ...prev, city: e.target.value }))}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="pro-disclosure">
                  Some professionals provide additional profile information through ClearBadge.
                </div>

                <button type="submit" className="primary search-btn" disabled={loading}>
                  {loading ? "Searching..." : "Search"}
                </button>
              </form>
            </div>

            {!showResultsPanel && (
              <div
                className="search-disclaimer-wrap"
                role="note"
                aria-label="Search disclaimer"
                data-reveal
                style={{ "--reveal-delay": "2060ms" }}
              >
                <p className="unverified-disclaimer">
                  Search results are based on selected criteria and available profile information. ClearBadge does not rank
                  or recommend professionals based on quality or suitability.
                </p>
              </div>
            )}

            {!embedded && !showResultsPanel && (
              <div className="hero-video-block hero-video-load-in">
                <HeroVideoOverlay
                  videoId={HERO_VIDEO_ID}
                  title="ClearBadge product walkthrough"
                  onOpen={() => setHeroVideoModalOpen(true)}
                  prefer1080
                />
              </div>
            )}

            {showResultsPanel && (
              <div className="panel results-panel">
                {gateState !== null && (
                  <div className="results-gate">
                    <div className="cb-loading-row">
                      <p className="muted cb-loading-text">
                        {gateState === "captcha" ? "Fetching results" : "Finding matches"}
                        <span className="cb-inline-spinner" aria-hidden="true" />
                      </p>
                    </div>
                    {TURNSTILE_SITE_KEY && gateState === "captcha" && (
                      <div className="turnstile-wrap" aria-label="Security check">
                        <div className="turnstile-card turnstile-card--loading">
                          <div className="turnstile-skeleton turnstile-skeleton--bar" aria-hidden="true" />
                          <TurnstileWidget
                            siteKey={TURNSTILE_SITE_KEY}
                            action="public_pro_search"
                            onToken={(token) => setTurnstileToken(String(token || ""))}
                            onError={handleTurnstileError}
                          />
                        </div>
                      </div>
                    )}
                    {TURNSTILE_SITE_KEY && gateState === "fetching" && (
                      <div className="turnstile-wrap" aria-hidden="true">
                        <div className="turnstile-card turnstile-card--placeholder turnstile-card--loading">
                          <div className="turnstile-skeleton turnstile-skeleton--bar" />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {gateState === null && loading && (
                  <p className="muted">
                    Searching <span className="cb-inline-spinner" aria-hidden="true" />
                  </p>
                )}
                {gateState === null && !loading && error && <p className="error-message">{error}</p>}
                {gateState === null && !loading && !error && (
                  <>
                    {debugMode && (
                      <p className="muted small" style={{ marginBottom: 8 }}>
                        Debug: {debugInfo ? JSON.stringify(debugInfo.counts || {}) : "no debug payload"}
                      </p>
                    )}
                    <p className="muted" style={{ marginBottom: 8 }}>
                      {filtered.length === 0
                        ? "No results found."
                        : `Found ${filtered.length} professional${filtered.length > 1 ? "s" : ""}.`}
                    </p>
                    {filtered.length === 0 && (
                      <div className="unverified-block" style={{ padding: 0, marginBottom: 12 }}>
                        <p className="unverified-disclaimer">
                          Can't find what you need? Invite a professional to claim and verify.
                        </p>
                        <div className="unverified-actions" style={{ marginTop: 10 }}>
                          <Link to="/invite" className="primary claim-cta">
                            Invite a professional
                          </Link>
                        </div>
                      </div>
                    )}

                    {sponsoredResults.length > 0 && (
                      <div className="sponsored-section">
                        <div className="sponsored-disclosure" role="note" aria-label="Sponsored professionals disclosure">
                          <div className="sponsored-disclosure-text">
                            <span className="sponsored-label">Sponsored</span>
                            <span>Top-of-category placement for Elite members.</span>
                          </div>
                          <span className="info-tooltip-wrap sponsored-tooltip">
                            <button
                              type="button"
                              className="info-tooltip-btn"
                              aria-label="About sponsored placement"
                              aria-expanded={openTooltipFor === "sponsored-disclosure"}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenTooltipFor((prev) => (prev === "sponsored-disclosure" ? null : "sponsored-disclosure"));
                              }}
                            >
                              <InfoIcon className="info-icon" />
                            </button>
                            <span
                              role="tooltip"
                              className={`info-tooltip ${openTooltipFor === "sponsored-disclosure" ? "open" : ""}`}
                            >
                              {SPONSORED_TOOLTIP_COPY}
                            </span>
                          </span>
                        </div>
                        <div className="person-list">
                          {sponsoredResults.map((person) =>
                            renderPersonCard(person, { tooltipKey: `s-${person.id}`, sponsored: true })
                          )}
                        </div>
                      </div>
                    )}

                    <div className="person-list">
                      {paginatedOrganicResults.map((person) => renderPersonCard(person, { tooltipKey: person.id }))}
                    </div>
                    {totalPages > 1 && (
                      <div className="pagination-bar">
                        <div className="pagination-pill">
                          <button
                            className="pagination-arrow"
                            type="button"
                            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                            disabled={currentPage <= 1}
                            aria-label="Previous page"
                          >
                            {"<"}
                          </button>
                          {paginationItems.map((item, idx) =>
                            item === "dots" ? (
                              <span key={`dots-${idx}`} className="pagination-dots">...</span>
                            ) : (
                              <button
                                key={item}
                                className={`pagination-page ${item === currentPage ? "active" : ""}`}
                                type="button"
                                onClick={() => setCurrentPage(item)}
                                aria-label={`Page ${item}`}
                              >
                                {item}
                              </button>
                            )
                          )}
                          <button
                            className="pagination-arrow"
                            type="button"
                            onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                            disabled={currentPage >= totalPages}
                            aria-label="Next page"
                          >
                            {">"}
                          </button>
                        </div>
                        <div className="pagination-size">
                          <select
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                            aria-label="Results per page"
                          >
                            {PAGE_SIZES.map((size) => (
                              <option key={size} value={size}>
                                {size} / page
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                    <p className="unverified-disclaimer results-footer-disclaimer" role="note" aria-label="Paid features disclosure">
                      {RESULTS_PAID_FEATURES_DISCLOSURE}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </section>
      </main>

      {!embedded && (
        <LandingFooter />
      )}

      {heroVideoModalOpen && (
        <div className="modal-backdrop hero-video-modal-backdrop" onClick={closeHeroVideoModal}>
          <div
            ref={heroVideoModalRef}
            className="modal hero-video-modal"
            role="dialog"
            aria-modal="true"
            aria-label="ClearBadge demo video"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              ref={heroVideoCloseRef}
              className="icon-btn hero-video-close"
              type="button"
              onClick={closeHeroVideoModal}
              aria-label="Close demo video"
            >
              x
            </button>
            <div className="hero-video-modal-frame">
              <iframe
                src={getYoutubeEmbedUrl(HERO_VIDEO_ID, true, true)}
                title="ClearBadge demo video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      {selectedProfile && (
        <div className="modal-backdrop profile-modal-backdrop" onClick={closeProfile}>
          <div
            ref={profileModalRef}
            className={`modal profile-modal${selectedProfile.isSponsored ? " sponsored-profile" : ""}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="public-find-pro-profile-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="profile-card-hero">
              <div className="profile-avatar">
                {selectedProfile.profilePhoto && String(selectedProfile.profilePhoto).startsWith("http") ? (
                  <img
                    src={selectedProfile.profilePhoto}
                    alt={selectedProfile.firstName || "Profile"}
                    onError={() => setSelectedProfile((prev) => (prev ? { ...prev, profilePhoto: null } : prev))}
                  />
                ) : (
                  <span>{initialsFrom(selectedProfile.firstName, selectedProfile.lastName)}</span>
                )}
              </div>
              {contactEnabled && (
                <button
                  type="button"
                  className={`contact-pro-pill ${profilePanel === "contact" ? "active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (profilePanel === "contact") {
                      setProfilePanel("details");
                      return;
                    }
                    setProfilePanel("contact");
                    setContactStep("form");
                    logLeadEvent("contact_pro_opened", selectedProfile);
                  }}
                  aria-label={profilePanel === "contact" ? "View profile details" : "Contact this professional"}
                >
                  <PhoneIcon className="contact-pro-icon" />
                  {profilePanel === "contact" ? "View profile" : "Contact Pro"}
                </button>
              )}
              <button
                ref={profileCloseButtonRef}
                className="icon-btn"
                onClick={closeProfile}
                aria-label="Close profile"
                type="button"
              >
                x
              </button>
            </div>
            {contactEnabled && profilePanel === "contact" ? (
              <div className="profile-contact-panel">
                <div className="lead-modal-header">
                  <div className="lead-title-block">
                    <p className="lead-eyebrow">Contact</p>
                    <h4 className="lead-name">
                      {`${selectedProfile.firstName || ""} ${selectedProfile.lastName || ""}`.trim() || "This professional"}
                    </h4>
                  </div>
                  <button
                    className="ghost small"
                    type="button"
                    onClick={() => {
                      setProfilePanel("details");
                      setContactStep("form");
                    }}
                    disabled={submittingContactLead}
                  >
                    Back
                  </button>
                </div>

                {selectedProfile.isSponsored && (
                  <p className="muted small" style={{ marginTop: -2, marginBottom: 12 }}>
                    Paid placement. Does not imply endorsement.
                  </p>
                )}

                <div className="contact-pro-subhead">
                  {selectedProfile.company && (
                    <p className="muted" style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <CompanyIcon className="card-line-icon" />
                      {selectedProfile.company}
                    </p>
                  )}
                  <div className="profile-contact" style={{ marginBottom: 12 }}>
                    {(selectedProfile.email || selectedProfile.emailDisplay) && (
                      <span>
                        <EmailIcon className="card-line-icon" />
                        {selectedProfile.email || selectedProfile.emailDisplay}
                      </span>
                    )}
                    {(selectedProfile.phone || selectedProfile.phoneDisplay) && (
                      <span>
                        <PhoneIcon className="card-line-icon" />
                        {selectedProfile.phone || selectedProfile.phoneDisplay}
                      </span>
                    )}
                  </div>
                </div>
                <div className="card-divider dotted" />

                {contactStep === "thanks" ? (
                  <div className="contact-thanks">
                    <h4 style={{ textAlign: "center" }}>Message sent</h4>
                    <p className="muted" style={{ textAlign: "center", marginTop: 8 }}>
                      Your message was sent. We'll be in touch soon.
                    </p>
                    <button
                      className="primary wide"
                      type="button"
                      onClick={() => {
                        setContactStep("form");
                        setProfilePanel("details");
                      }}
                      style={{ marginTop: 16 }}
                    >
                      Back to profile
                    </button>
                  </div>
                ) : (
                  <div className="lead-form">
                    <label>
                      <span>Your name</span>
                      <input
                        type="text"
                        value={contactLeadName}
                        onChange={(e) => setContactLeadName(e.target.value)}
                        placeholder="Your name"
                      />
                    </label>
                    <label>
                      <span>Your email</span>
                      <input
                        type="email"
                        value={contactLeadEmail}
                        onChange={(e) => setContactLeadEmail(e.target.value)}
                        placeholder="you@example.com"
                      />
                    </label>
                    <label>
                      <span>Your phone number</span>
                      <input
                        type="tel"
                        value={contactLeadPhone}
                        onChange={(e) => setContactLeadPhone(formatPhoneInput(e.target.value))}
                        placeholder="(555) 123-4567"
                      />
                    </label>
                    <label>
                      <span>Inquiry Details</span>
                      <textarea
                        value={contactLeadNotes}
                        onChange={(e) => setContactLeadNotes(e.target.value)}
                        placeholder="What can this professional help with?"
                        rows={4}
                      />
                    </label>
                    <p className="lead-privacy">
                      <span aria-hidden="true" className="lead-lock">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          className="lead-lock-icon"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
                          />
                        </svg>
                      </span>
                      Your data will always be kept private.
                    </p>
                    <button
                      className="primary wide"
                      type="button"
                      onClick={async () => {
                        await logLeadEvent("contact_form_submitted", selectedProfile);
                        await submitContactLead(selectedProfile);
                        setContactStep("thanks");
                      }}
                      disabled={submittingContactLead || !selectedProfile.publicSlug}
                    >
                      {submittingContactLead ? "Sending..." : "Send"}
                    </button>
                    <p className="unverified-disclaimer" style={{ marginTop: 10 }}>
                      By submitting this form, you agree that your information will be shared directly with the selected
                      professional so they may contact you regarding your inquiry. ClearBadge does not endorse or guarantee
                      services provided.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="profile-card-body">
                  <div className="profile-info-top" style={{ alignItems: "flex-start" }}>
                    <div>
                      <h2 id="public-find-pro-profile-title">{`${selectedProfile.firstName || ""} ${selectedProfile.lastName || ""}`.trim() || "Professional"}</h2>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {selectedProfile.isSponsored && <div className="sponsored-badge">Sponsored</div>}
                      {renderVerificationPill(selectedProfile, `m-${selectedProfile.id}`)}
                    </div>
                  </div>
                  {selectedProfile.isSponsored && (
                    <p className="muted small" style={{ marginTop: -6, marginBottom: 10 }}>
                      Paid placement. Does not imply endorsement.
                    </p>
                  )}
                  {selectedProfile.company && (
                    <p className="muted" style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <CompanyIcon className="card-line-icon" />
                      {selectedProfile.company}
                    </p>
                  )}
                  <div className="profile-contact">
                    {(selectedProfile.email || selectedProfile.emailDisplay) && (
                      <span>
                        <EmailIcon className="card-line-icon" />
                        {selectedProfile.email || selectedProfile.emailDisplay}
                      </span>
                    )}
                    {(selectedProfile.phone || selectedProfile.phoneDisplay) && (
                      <span>
                        <PhoneIcon className="card-line-icon" />
                        {selectedProfile.phone || selectedProfile.phoneDisplay}
                      </span>
                    )}
                    {selectedProfile.states.length > 0 && (
                      <span>
                        <LicenseIcon className="card-line-icon" />
                        States: {selectedProfile.states.join(", ")}
                      </span>
                    )}
                  </div>
                  <div className="card-divider dotted" />
                </div>

                {selectedProfile.hasUnverified && !selectedProfile.hasVerified && (
                  <div className="unverified-block">
                    <p className="unverified-disclaimer">
                      This profile is automatically generated from publicly available state licensing databases. This individual
                      has not claimed or verified this information with ClearBadge.
                    </p>
                    <div className="unverified-actions">
                      <a href="mailto:support@clearbadge.org" className="disclaimer-link">
                        Request correction or removal
                      </a>
                      <Link to="/login?mode=signup" className="primary claim-cta">
                        Claim & Get Verified
                      </Link>
                    </div>
                  </div>
                )}

                {!selectedProfile.hasUnverified || selectedProfile.hasVerified ? (
                  <div className="profile-license-list">
                    {sortLicensesByState(selectedProfile.licenses).map((lic) => {
                      const licenseKey = getLicenseKey(lic);
                      const docStatus = licenseDocStatus[licenseKey];
                      return (
                        <button
                          type="button"
                          key={licenseKey}
                          className={`profile-license-card${selectedProfile.isSponsored ? " sponsored-license-card" : ""}`}
                          aria-label={`Open license document for ${lic.license_number || lic.licenseNumber || "license"}`}
                          onClick={async () => {
                            await handleLicenseOpen(lic, licenseKey);
                          }}
                        >
                          <div className="license-top license-top-row">
                            <div className="pill-with-icon">
                              <p className="pill">{lic.industry || "Record"}</p>
                            </div>
                            {lic.verified ? (
                              <div className="license-verify-badge vettify-pill-badge">
                                <span className="vettify-pill__icon small-dot">
                                  <img src={clearBadgeMarkWhite} alt="" />
                                </span>
                                <span className="vettify-pill__text">Verified</span>
                              </div>
                            ) : lic.is_unverified ? (
                              <div className="license-unverified-badge">
                                <span className="small-dot">
                                  <img src={clearBadgeMarkWhite} alt="ClearBadge" />
                                </span>
                                <span className="vettify-pill__text">Not Verified</span>
                              </div>
                            ) : null}
                          </div>
                          <h4>License # {lic.license_number || lic.licenseNumber || "Unavailable"}</h4>
                          <p className="muted ellipsis">State: {lic.state || "Unavailable"}</p>
                          <div className="license-meta" style={{ gap: 10 }}>
                            {(lic.email || lic.email_display || lic.emailDisplay) && (
                              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <EmailIcon className="card-line-icon" />
                                {lic.email || lic.email_display || lic.emailDisplay}
                              </span>
                            )}
                            {(lic.phone || lic.phone_display || lic.phoneDisplay) && (
                              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <PhoneIcon className="card-line-icon" />
                                {lic.phone || lic.phone_display || lic.phoneDisplay}
                              </span>
                            )}
                            {lic.company_agency && (
                              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <CompanyIcon className="card-line-icon" />
                                {lic.company_agency}
                              </span>
                            )}
                          </div>
                          {lic.verified && (
                            <p className="muted" style={{ marginTop: 8, fontWeight: 600 }}>
                              {docStatus === "missing" ? "No document on file." : "Click to view license ->"}
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}



